import appsConfigData from '@/data/apps-config.json';
import privacyTemplate from '@/templates/privacy-policy.html?raw';

interface AppConfig {
  name: string;
  description: string;
  version: string;
  effectiveDate: string;
  lastUpdated: string;
  contactEmail: string;
  contactAddress?: string | null;
  collectedInfo: string[];
  autoCollectedInfo: string[];
  usagePurposes: string[];
  dataSharingPolicy: string;
  thirdPartyServices?: {
    services: Array<{
      name: string;
      purpose: string;
    }>;
  } | null;
  dataSecurityPolicy: string;
  dataRetentionPolicy: string;
  childrenPrivacyPolicy: string;
}

interface AppsConfig {
  [appName: string]: AppConfig;
}

class PageRenderer {
  private appsConfig: AppsConfig;
  private template: string;

  constructor() {
    this.appsConfig = appsConfigData as unknown as AppsConfig;
    this.template = privacyTemplate;
  }

  renderPrivacyPolicy(appName: string): void {
    // eslint-disable-next-line no-console
    console.log('Rendering privacy policy for:', appName);

    const appConfig = this.appsConfig[appName];

    if (!appConfig) {
      // eslint-disable-next-line no-console
      console.log('App config not found for:', appName);
      this.render404(`App "${appName}" not found`);
      return;
    }

    // eslint-disable-next-line no-console
    console.log('Processing template for app:', appConfig.name);
    const renderedHtml = this.processTemplate(this.template, appConfig);
    this.renderPage(renderedHtml);
  }

  private processTemplate(template: string, config: AppConfig): string {
    let processed = template;

    // Replace simple variables
    processed = processed.replace(/\{\{APP_NAME\}\}/g, config.name);
    processed = processed.replace(/\{\{APP_VERSION\}\}/g, config.version);
    processed = processed.replace(/\{\{EFFECTIVE_DATE\}\}/g, config.effectiveDate);
    processed = processed.replace(/\{\{LAST_UPDATED\}\}/g, config.lastUpdated);
    processed = processed.replace(/\{\{CONTACT_EMAIL\}\}/g, config.contactEmail);
    processed = processed.replace(/\{\{DATA_SHARING_POLICY\}\}/g, config.dataSharingPolicy);
    processed = processed.replace(/\{\{DATA_SECURITY_POLICY\}\}/g, config.dataSecurityPolicy);
    processed = processed.replace(/\{\{DATA_RETENTION_POLICY\}\}/g, config.dataRetentionPolicy);
    processed = processed.replace(/\{\{CHILDREN_PRIVACY_POLICY\}\}/g, config.childrenPrivacyPolicy);

    // Handle conditional contact address
    if (config.contactAddress) {
      processed = processed.replace(/\{\{#CONTACT_ADDRESS\}\}/g, '');
      processed = processed.replace(/\{\{\/CONTACT_ADDRESS\}\}/g, '');
      processed = processed.replace(/\{\{CONTACT_ADDRESS\}\}/g, config.contactAddress);
    } else {
      // Remove the entire contact address section
      processed = processed.replace(
        /\{\{#CONTACT_ADDRESS\}\}[\s\S]*?\{\{\/CONTACT_ADDRESS\}\}/g,
        ''
      );
    }

    // Handle arrays
    processed = this.processArray(processed, 'COLLECTED_INFO', config.collectedInfo);
    processed = this.processArray(processed, 'AUTO_COLLECTED_INFO', config.autoCollectedInfo);
    processed = this.processArray(processed, 'USAGE_PURPOSES', config.usagePurposes);

    // Handle third-party services
    if (config.thirdPartyServices) {
      processed = processed.replace(/\{\{#THIRD_PARTY_SERVICES\}\}/g, '');
      processed = processed.replace(/\{\{\/THIRD_PARTY_SERVICES\}\}/g, '');
      processed = this.processServices(processed, config.thirdPartyServices.services);
    } else {
      processed = processed.replace(
        /\{\{#THIRD_PARTY_SERVICES\}\}[\s\S]*?\{\{\/THIRD_PARTY_SERVICES\}\}/g,
        ''
      );
    }

    return processed;
  }

  private processArray(template: string, arrayName: string, items: string[]): string {
    const startPattern = new RegExp(`\\{\\{#${arrayName}\\}\\}`, 'g');
    const endPattern = new RegExp(`\\{\\{\\/${arrayName}\\}\\}`, 'g');

    // Check if items is undefined or null
    if (!items || !Array.isArray(items)) {
      return template.replace(startPattern, '').replace(endPattern, '');
    }

    const listItems = items.map(item => `<li>${item}</li>`).join('\n                    ');

    return template
      .replace(startPattern, '')
      .replace(endPattern, '')
      .replace(new RegExp(`\\{\\{\\.\\}\\}`, 'g'), listItems);
  }

  private processServices(
    template: string,
    services: Array<{ name: string; purpose: string }>
  ): string {
    const startPattern = /\{\{#SERVICES\}\}/g;
    const endPattern = /\{\{\/SERVICES\}\}/g;

    const serviceItems = services
      .map(service => `<li><strong>${service.name}:</strong> ${service.purpose}</li>`)
      .join('\n                    ');

    return template
      .replace(startPattern, '')
      .replace(endPattern, '')
      .replace(/\{\{name\}\}/g, serviceItems)
      .replace(/\{\{purpose\}\}/g, '');
  }

  private renderPage(html: string): void {
    document.documentElement.innerHTML = html;
    // Re-execute scripts logic if needed, but for privacy policy usually not needed.
    // If strict mode prevents document.write, this is safer.
  }

  private render404(message: string): void {
    const html404 = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Page Not Found - DevMeUp</title>
          <style>
              body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  margin: 0;
                  background-color: #f8fafc;
                  color: #2d3748;
              }
              .container {
                  text-align: center;
                  padding: 2rem;
              }
              h1 {
                  font-size: 3rem;
                  margin-bottom: 1rem;
                  color: #667eea;
              }
              p {
                  font-size: 1.25rem;
                  margin-bottom: 2rem;
              }
              a {
                  display: inline-block;
                  background-color: #667eea;
                  color: white;
                  padding: 0.75rem 1.5rem;
                  text-decoration: none;
                  border-radius: 5px;
                  transition: background-color 0.3s ease;
              }
              a:hover {
                  background-color: #5a67d8;
              }
          </style>
      </head>
      <body>
          <div class="container">
              <h1>404</h1>
              <p>${message}</p>
              <a href="/">← Back to DevMeUp</a>
          </div>
      </body>
      </html>
    `;

    this.renderPage(html404);
  }

  renderPage404(): void {
    this.render404('Page not found');
  }
}

export default PageRenderer;
